/**
 * Computes a stable device fingerprint from browser properties.
 * Identical in regular Chrome and incognito Chrome on the same machine.
 * Used for trusted device MFA bypass.
 */
export async function computeDeviceFingerprint(): Promise<string> {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height + 'x' + screen.colorDepth,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.platform ?? '',
    navigator.hardwareConcurrency?.toString() ?? '',
    (navigator as any).deviceMemory?.toString() ?? '',
  ].join('|');

  // SHA-256 the components for a fixed-length stable hash
  try {
    const encoded = new TextEncoder().encode(components);
    const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Fallback: base64 of raw string (SHA-256 unavailable in some contexts)
    return btoa(unescape(encodeURIComponent(components))).slice(0, 64);
  }
}

/**
 * Generates a cryptographically random device token (64 hex chars).
 * Stored in localStorage and validated against the trusted_devices table.
 */
export function generateDeviceToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generates a human-readable device label from the browser/OS.
 * e.g. "Chrome on Windows", "Safari on iPad", "Firefox on macOS"
 */
export function generateDeviceLabel(): string {
  const ua = navigator.userAgent;
  let browser = 'Browser';
  let os = 'Unknown';

  if (ua.includes('Chrome') && !ua.includes('Edg') && !ua.includes('OPR')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';

  if (ua.includes('iPad')) os = 'iPad';
  else if (ua.includes('iPhone')) os = 'iPhone';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('Mac OS X') && !ua.includes('iPhone') && !ua.includes('iPad')) os = 'macOS';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Linux')) os = 'Linux';

  return `${browser} on ${os}`;
}

// localStorage key for the device token (per-tenant)
export const DEVICE_TOKEN_KEY = (tenantId: string) => `helix_device_token_${tenantId}`;
